import { useCallback, useEffect, useRef } from 'react'

import { useEventListener } from '../../hooks'

/**
 * Позволяет менять положение элемента с помощью перетаскивания
 *
 * @param element корневой элемент, который будет двигаться
 * @param anchorElement элемент, за который надо тянуть, чтобы движение происходило. Если не передан, используется корневой элемент
 * @param parentElement элемент, ограничивающий перемещение. Если не передан, используется родитель корневого элемента
 */
export const useMove = (
  element?: HTMLDivElement,
  anchorElement?: HTMLElement,
  parentElement?: HTMLElement,
): void => {
  const offsetX = useRef<number>(0)
  const offsetY = useRef<number>(0)
  const elementSize = useRef<DOMRect>()
  const parentSize = useRef<DOMRect>()

  const updateSizes = useCallback(() => {
    if (!element) return

    elementSize.current = element.getBoundingClientRect()
    parentSize.current = (parentElement ?? element.parentElement)?.getBoundingClientRect()
  }, [element, parentElement])

  useEffect(() => {
    updateSizes()
  }, [element, updateSizes])

  useEventListener('resize', updateSizes)

  useEffect(() => {
    function alignCenter() {
      if (!element || !elementSize.current || !parentSize.current) return

      const { width, height } = elementSize.current
      const { left, top, height: parentHeight, width: parentWidth } = parentSize.current
      element.style.top = `${top + (parentHeight - height) / 2}px`
      element.style.left = `${left + (parentWidth - width) / 2}px`
    }

    if (element) {
      alignCenter()
    }
  }, [element])

  const move = useCallback(
    ({ pageX, pageY }: MouseEvent) => {
      if (!element) return

      element.style.left = `${pageX - offsetX.current}px`
      element.style.top = `${pageY - offsetY.current}px`
    },
    [element],
  )

  const start = useCallback(
    ({ clientX, clientY }: MouseEvent) => {
      if (!element) return

      const { left, top } = element.getBoundingClientRect()
      offsetX.current = clientX - left
      offsetY.current = clientY - top
      window.addEventListener('mousemove', move)
    },
    [element, move],
  )

  const end = useCallback(() => {
    if (!element) return

    window.removeEventListener('mousemove', move)
  }, [element, move])

  useEffect(() => {
    if (!element) return

    const anchor = anchorElement ?? element
    anchor.addEventListener('mousedown', start)
    anchor.addEventListener('mouseup', end)

    return () => {
      anchor.removeEventListener('mousedown', start)
      anchor.removeEventListener('mouseup', end)
    }
  }, [element, anchorElement, start, end])
}
